import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaPlay,
  FaPause,
  FaStop,
  FaSave,
  FaTrash,
  FaMicrophone,
  FaClock,
  FaVolumeMute,
  FaVolumeUp,
} from "react-icons/fa";
import { Databases, ID, Query } from "appwrite";
import client from "./appwriteConfig";
import SoundFx from "./SoundFx";
import SoundFxControls from "./SoundFxControls";
import { useUser } from "./UserContext";
import {
  getStorage,
  ref as firebaseRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { app } from "./firebaseConfig";
import "./css/VocalsSeq.css";

const databases = new Databases(client);
const firebaseStorage = getStorage(app);

const VOCAL_STEP_OPTIONS = [16, 32, 64];

const VocalsSeq = ({ bpm = 120, stepCount = 16 }) => {
  const { user } = useUser();

  const [vocals, setVocals] = useState([]);
  const [savedVocals, setSavedVocals] = useState([]);
  const [selectedSavedVocalId, setSelectedSavedVocalId] = useState("");
  const [isLoadingVocals, setIsLoadingVocals] = useState(false);
  const [vocalStepCount, setVocalStepCount] = useState(() =>
    VOCAL_STEP_OPTIONS.includes(stepCount) ? stepCount : 16,
  );

  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);

  const [playingVocalId, setPlayingVocalId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [micLevel, setMicLevel] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const audioRef = useRef(null);
  const vocalFxAudioContextRef = useRef(null);
  const vocalFxSourceRef = useRef(null);
  const vocalFxChainRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const rafRef = useRef(null);

  const recordingTimerRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const recordingStartedAtRef = useRef(0);
  const vocalsRef = useRef([]);
  /*
    One sequencer step is a sixteenth note.

    At 120 BPM:
    16 steps = 4 beats = 2 seconds
    32 steps = 8 beats = 4 seconds
    64 steps = 16 beats = 8 seconds
  */ useEffect(() => {
    if (user?.email) {
      fetchSavedVocals();
    } else {
      setSavedVocals([]);
      setSelectedSavedVocalId("");
    }
  }, [user?.email]);
  useEffect(() => {
    vocalsRef.current = vocals;
  }, [vocals]);
  const stepDurationMs = useMemo(() => {
    const safeBpm = Math.max(1, Number(bpm) || 120);
    return ((60 / safeBpm) * 1000) / 4;
  }, [bpm]);

  const maximumRecordingMs = useMemo(() => {
    return vocalStepCount * stepDurationMs;
  }, [vocalStepCount, stepDurationMs]);

  const recordingProgress = Math.min(
    100,
    maximumRecordingMs > 0
      ? (recordingElapsedMs / maximumRecordingMs) * 100
      : 0,
  );

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.max(0, milliseconds) / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const tenths = Math.floor((totalSeconds % 1) * 10);

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}.${tenths}`;
  };

  const uploadVocalToFirebase = async (fileBlob, fileName) => {
    if (!user?.email) {
      throw new Error("User email is unavailable.");
    }

    const safeEmail = user.email.replace(/[^a-zA-Z0-9@._-]/g, "_");
    const filePath = `beatmaker/${safeEmail}/vocals/${fileName}`;

    const fileRef = firebaseRef(firebaseStorage, filePath);

    await uploadBytes(fileRef, fileBlob);

    const audioUrl = await getDownloadURL(fileRef);

    return {
      audioUrl,
      firebasePath: filePath,
    };
  };

  const stopMicMeter = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setMicLevel(0);
  };

  const stopMicrophoneStream = () => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });

    streamRef.current = null;
  };

  const clearRecordingTimers = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  };

  const startMicMeter = (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      return;
    }

    const audioContext = new AudioContextClass();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateMeter = () => {
      if (!analyserRef.current) return;

      analyser.getByteFrequencyData(dataArray);

      const average =
        dataArray.reduce((total, value) => total + value, 0) / dataArray.length;

      const level = Math.min(100, Math.round((average / 255) * 140));

      setMicLevel(level);

      rafRef.current = requestAnimationFrame(updateMeter);
    };

    updateMeter();
  };

  const stopRecording = () => {
    clearRecordingTimers();

    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      stopMicrophoneStream();
      stopMicMeter();
      setIsRecording(false);
    }
  };

  const getSupportedMimeType = () => {
    const supportedTypes = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];

    return (
      supportedTypes.find((type) => {
        return (
          typeof MediaRecorder !== "undefined" &&
          MediaRecorder.isTypeSupported(type)
        );
      }) || ""
    );
  };
  const generateWaveform = async (audioSource, barCount = 64) => {
    if (!audioSource) return [];

    try {
      let arrayBuffer;

      if (audioSource instanceof Blob) {
        arrayBuffer = await audioSource.arrayBuffer();
      } else {
        const response = await fetch(audioSource);

        if (!response.ok) {
          throw new Error("Unable to fetch vocal audio.");
        }

        arrayBuffer = await response.arrayBuffer();
      }

      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) return [];

      const audioContext = new AudioContextClass();

      const audioBuffer = await audioContext.decodeAudioData(
        arrayBuffer.slice(0),
      );

      const channelData = audioBuffer.getChannelData(0);

      const samplesPerBar = Math.max(
        1,
        Math.floor(channelData.length / barCount),
      );

      const waveform = [];

      for (let barIndex = 0; barIndex < barCount; barIndex += 1) {
        const start = barIndex * samplesPerBar;

        const end = Math.min(start + samplesPerBar, channelData.length);

        let peak = 0;

        for (let sampleIndex = start; sampleIndex < end; sampleIndex += 1) {
          peak = Math.max(peak, Math.abs(channelData[sampleIndex]));
        }

        waveform.push(peak);
      }

      const maximumPeak = Math.max(...waveform, 0.01);

      await audioContext.close();

      return waveform.map((peak) => {
        const normalized = peak / maximumPeak;

        return Math.max(8, Math.round(normalized * 40));
      });
    } catch (error) {
      console.error("Unable to generate vocal waveform:", error);

      return Array.from({ length: barCount }, () => 8);
    }
  };
  const startRecording = async () => {
    if (!user?.email) {
      alert("Please login to record vocals.");
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Microphone recording is not supported in this browser.");
      return;
    }

    try {
      stopVocal();

      clearRecordingTimers();
      stopMicrophoneStream();
      stopMicMeter();

      setRecordingElapsedMs(0);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;
      chunksRef.current = [];

      startMicMeter(stream);

      const mimeType = getSupportedMimeType();

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      const recordingStepCount = vocalStepCount;
      const recordingMaximumMs = recordingStepCount * stepDurationMs;
      const recordingMimeType = recorder.mimeType || mimeType || "audio/webm";

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        console.error("Vocal recorder error:", event.error || event);
        clearRecordingTimers();
        stopMicrophoneStream();
        stopMicMeter();
        setIsRecording(false);
      };

      recorder.onstop = () => {
        clearRecordingTimers();

        const elapsedMs = Math.min(
          Date.now() - recordingStartedAtRef.current,
          recordingMaximumMs,
        );

        const blob = new Blob(chunksRef.current, {
          type: recordingMimeType,
        });
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);

          generateWaveform(blob).then((waveform) => {
            const newVocal = {
              id: Date.now().toString(),
              name: `Vocal ${vocals.length + 1}`,
              blob,
              localUrl: url,
              audioUrl: "",
              firebasePath: "",
              stepCount: recordingStepCount,
              durationMs: elapsedMs,
              bpm: Number(bpm) || 120,
              startStep: 0,
              muted: false,
              volume,
              saved: false,
              waveform,
              effects: SoundFx.getDefaultEffects(),
            };

            setVocals((previousVocals) => [newVocal, ...previousVocals]);
          });
        }

        chunksRef.current = [];
        mediaRecorderRef.current = null;

        stopMicrophoneStream();
        stopMicMeter();

        setRecordingElapsedMs(elapsedMs);
        setIsRecording(false);
      };

      recorder.start(100);

      recordingStartedAtRef.current = Date.now();

      setIsRecording(true);
      setRecordingElapsedMs(0);

      recordingTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - recordingStartedAtRef.current;

        setRecordingElapsedMs(Math.min(elapsed, recordingMaximumMs));
      }, 50);

      autoStopTimerRef.current = setTimeout(() => {
        stopRecording();
      }, recordingMaximumMs);
    } catch (error) {
      console.error("Unable to start vocal recording:", error);

      clearRecordingTimers();
      stopMicrophoneStream();
      stopMicMeter();

      setIsRecording(false);
      setRecordingElapsedMs(0);

      if (error?.name === "NotAllowedError") {
        alert(
          "Microphone permission was denied. Please allow microphone access and try again.",
        );
      } else if (error?.name === "NotFoundError") {
        alert("No microphone was found.");
      } else {
        alert("Unable to start vocal recording.");
      }
    }
  };

  const pauseVocal = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    setIsPlaying(false);
  };

  const resumeVocal = async () => {
    if (!audioRef.current) return;

    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error("Unable to resume vocal:", error);
    }
  };

  const playVocal = async (vocal) => {
    if (!vocal) return;

    if (playingVocalId === vocal.id && audioRef.current) {
      if (isPlaying) {
        pauseVocal();
      } else {
        await resumeVocal();
      }

      return;
    }

    stopVocal();

    const sourceUrl = vocal.audioUrl || vocal.localUrl;

    if (!sourceUrl) {
      alert("This vocal does not have an audio source.");
      return;
    }

    try {
      const AudioContextClass =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContextClass) {
        throw new Error("Web Audio is not supported.");
      }

      if (!vocalFxAudioContextRef.current) {
        vocalFxAudioContextRef.current = new AudioContextClass();
      }

      const audioContext = vocalFxAudioContextRef.current;

      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const audio = new Audio(sourceUrl);

      audio.crossOrigin = "anonymous";
      audio.preload = "auto";
      audio.volume = 1;

      audioRef.current = audio;

      const mediaSource = audioContext.createMediaElementSource(audio);

      vocalFxSourceRef.current = mediaSource;

      const vocalVolume =
        muted || vocal.muted
          ? 0
          : Math.min(1, Math.max(0, vocal.volume ?? volume));

      const fxChain = SoundFx.connect({
        audioContext,
        inputNode: mediaSource,
        destinationNode: audioContext.destination,
        effects: vocal.effects || SoundFx.getDefaultEffects(),
        volume: vocalVolume,
      });

      vocalFxChainRef.current = fxChain;

      audio.onplay = () => {
        setPlayingVocalId(vocal.id);
        setIsPlaying(true);
      };

      audio.onpause = () => {
        if (!audio.ended) {
          setIsPlaying(false);
        }
      };

      audio.onended = () => {
        vocalFxChainRef.current?.disconnect?.();

        vocalFxChainRef.current = null;
        vocalFxSourceRef.current = null;
        audioRef.current = null;

        setPlayingVocalId(null);
        setIsPlaying(false);
      };

      audio.onerror = () => {
        vocalFxChainRef.current?.disconnect?.();

        vocalFxChainRef.current = null;
        vocalFxSourceRef.current = null;
        audioRef.current = null;

        setPlayingVocalId(null);
        setIsPlaying(false);

        alert("Unable to play this vocal.");
      };

      await audio.play();
    } catch (error) {
      console.error("Unable to play vocal with effects:", error);

      vocalFxChainRef.current?.disconnect?.();

      vocalFxChainRef.current = null;
      vocalFxSourceRef.current = null;
      audioRef.current = null;

      setPlayingVocalId(null);
      setIsPlaying(false);
    }
  };

  const stopVocal = () => {
    if (audioRef.current) {
      audioRef.current.pause();

      try {
        audioRef.current.currentTime = 0;
      } catch {}

      audioRef.current = null;
    }

    vocalFxChainRef.current?.disconnect?.();

    vocalFxChainRef.current = null;
    vocalFxSourceRef.current = null;

    setPlayingVocalId(null);
    setIsPlaying(false);
  };

  const updateVocalVolume = (vocalId, newVolume) => {
    setVocals((previousVocals) =>
      previousVocals.map((vocal) =>
        vocal.id === vocalId
          ? {
              ...vocal,
              volume: newVolume,
            }
          : vocal,
      ),
    );

    if (playingVocalId === vocalId && vocalFxChainRef.current) {
      const playingVocal = vocals.find((vocal) => vocal.id === vocalId);

      vocalFxChainRef.current.update(
        playingVocal?.effects || SoundFx.getDefaultEffects(),
        muted || playingVocal?.muted ? 0 : newVolume,
      );
    }
  };
  const updateVocalEffects = (vocalId, nextEffects) => {
    let updatedVocal = null;

    setVocals((previousVocals) =>
      previousVocals.map((vocal) => {
        if (vocal.id !== vocalId) {
          return vocal;
        }

        updatedVocal = {
          ...vocal,
          effects: nextEffects,
        };

        return updatedVocal;
      }),
    );

    /*
    Rebuild the audio chain when an effect is
    switched on or off.

    SoundFx.update() can update existing sliders,
    but it cannot add a node that did not exist
    when playback began.
  */
    if (playingVocalId === vocalId && audioRef.current && updatedVocal) {
      const currentTime = audioRef.current.currentTime;

      const wasPlaying = !audioRef.current.paused;

      stopVocal();

      window.setTimeout(async () => {
        await playVocal(updatedVocal);

        if (audioRef.current) {
          try {
            audioRef.current.currentTime = currentTime;

            if (!wasPlaying) {
              audioRef.current.pause();
              setIsPlaying(false);
            }
          } catch (error) {
            console.error("Unable to restore vocal position:", error);
          }
        }
      }, 30);
    }
  };
  const toggleVocalMute = (vocalId) => {
    let nextMutedValue = false;
    let updatedVocal = null;

    setVocals((previousVocals) =>
      previousVocals.map((vocal) => {
        if (vocal.id !== vocalId) return vocal;

        nextMutedValue = !vocal.muted;

        updatedVocal = {
          ...vocal,
          muted: nextMutedValue,
        };

        return updatedVocal;
      }),
    );

    if (playingVocalId === vocalId && vocalFxChainRef.current && updatedVocal) {
      vocalFxChainRef.current.update(
        updatedVocal.effects || SoundFx.getDefaultEffects(),
        muted || nextMutedValue ? 0 : Number(updatedVocal.volume ?? volume),
      );
    }
  };
  const fetchSavedVocals = async () => {
    if (!user?.email) return;

    setIsLoadingVocals(true);

    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_VOCALS_ID,
        [
          Query.equal("userEmail", user.email),
          Query.orderDesc("$createdAt"),
          Query.limit(100),
        ],
      );

      const loadedVocals = await Promise.all(
        response.documents.map(async (document) => {
          const waveform = document.audioUrl
            ? await generateWaveform(document.audioUrl)
            : [];
          let savedEffects = SoundFx.getDefaultEffects();

          try {
            if (document.effectsJson) {
              savedEffects = SoundFx.getDefaultEffects(
                JSON.parse(document.effectsJson),
              );
            }
          } catch (error) {
            console.error("Could not parse vocal effects:", error);
          }
          return {
            id: document.$id,
            documentId: document.$id,
            name:
              document.vocalName ||
              document.patternName ||
              document.name ||
              "Untitled Vocal",
            blob: null,
            localUrl: "",
            audioUrl: document.audioUrl || "",
            firebasePath: document.firebasePath || "",
            stepCount: Number(document.stepCount) || 16,
            durationMs: Number(document.durationMs) || 0,
            bpm: Number(document.bpm) || 120,
            startStep: Number(document.startStep) || 0,
            muted: false,
            volume: Number(document.volume ?? 0.8),
            saved: true,
            waveform,
            effects: savedEffects,
          };
        }),
      );

      setSavedVocals(loadedVocals);
    } catch (error) {
      console.error("Unable to fetch saved vocals:", error);
    } finally {
      setIsLoadingVocals(false);
    }
  };
  const loadSavedVocal = (vocalId) => {
    setSelectedSavedVocalId(vocalId);

    if (!vocalId) return;

    const selectedVocal = savedVocals.find((vocal) => vocal.id === vocalId);

    if (!selectedVocal) return;

    setVocals((previousVocals) => {
      const alreadyLoaded = previousVocals.some(
        (vocal) => vocal.id === selectedVocal.id,
      );

      if (alreadyLoaded) {
        return previousVocals;
      }

      return [selectedVocal, ...previousVocals];
    });
  };
  const createProcessedVocalBlob = async (vocal) => {
    const source = vocal.localUrl || vocal.audioUrl || vocal.blob;

    if (!source) {
      throw new Error("Vocal source is unavailable.");
    }

    return SoundFx.render({
      source,
      effects: vocal.effects || SoundFx.getDefaultEffects(),
      volume: Number(vocal.volume ?? 1),
    });
  };
  const saveVocal = async (vocal) => {
    if (!vocal) return;

    if (!user?.email) {
      alert("Please login before saving vocals.");
      return;
    }

    try {
      stopVocal();

      const processedBlob = await createProcessedVocalBlob(vocal);

      const safeName =
        vocal.name.trim().replace(/[^a-zA-Z0-9_-]/g, "_") || "Vocal";

      const fileName = `${safeName}_processed_${Date.now()}.wav`;

      const { audioUrl, firebasePath } = await uploadVocalToFirebase(
        processedBlob,
        fileName,
      );

      const documentData = {
        userEmail: user.email,
        vocalName: vocal.name,
        audioUrl,
        firebasePath,
        stepCount: Number(vocal.stepCount) || 16,
        bpm: Number(vocal.bpm || bpm),
        volume: Number(vocal.volume ?? 0.8),
        durationMs: Math.round(vocal.durationMs || 0),
        effectsJson: JSON.stringify(
          vocal.effects || SoundFx.getDefaultEffects(),
        ),
      };

      let savedDocumentId = vocal.documentId || (vocal.saved ? vocal.id : null);

      if (savedDocumentId) {
        await databases.updateDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_VOCALS_ID,
          savedDocumentId,
          documentData,
        );
      } else {
        const savedDocument = await databases.createDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_VOCALS_ID,
          ID.unique(),
          documentData,
        );

        savedDocumentId = savedDocument.$id;
      }

      if (vocal.localUrl) {
        URL.revokeObjectURL(vocal.localUrl);
      }

      const processedLocalUrl = URL.createObjectURL(processedBlob);

      const waveform = await generateWaveform(processedBlob);

      setVocals((previousVocals) =>
        previousVocals.map((currentVocal) =>
          currentVocal.id === vocal.id
            ? {
                ...currentVocal,
                id: savedDocumentId,
                documentId: savedDocumentId,
                blob: processedBlob,
                localUrl: processedLocalUrl,
                audioUrl,
                firebasePath,
                waveform,
                saved: true,
              }
            : currentVocal,
        ),
      );

      await fetchSavedVocals();

      alert("Vocal effects processed and saved.");
    } catch (error) {
      console.error("Unable to process and save vocal:", error);

      alert("Unable to process and save this vocal.");
    }
  };

  const deleteVocal = (id) => {
    if (playingVocalId === id) {
      stopVocal();
    }

    setVocals((previousVocals) => {
      const vocalToDelete = previousVocals.find((vocal) => vocal.id === id);

      if (vocalToDelete?.localUrl) {
        URL.revokeObjectURL(vocalToDelete.localUrl);
      }

      return previousVocals.filter((vocal) => vocal.id !== id);
    });
  };

  useEffect(() => {
    if (!playingVocalId || !vocalFxChainRef.current) {
      return;
    }

    const playingVocal = vocals.find((vocal) => vocal.id === playingVocalId);

    if (!playingVocal) return;

    vocalFxChainRef.current.update(
      playingVocal.effects || SoundFx.getDefaultEffects(),
      muted || playingVocal.muted ? 0 : Number(playingVocal.volume ?? volume),
    );
  }, [muted, volume, vocals, playingVocalId]);
  useEffect(() => {
    return () => {
      clearRecordingTimers();

      const recorder = mediaRecorderRef.current;

      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch (error) {
          console.error("Unable to stop recorder during cleanup:", error);
        }
      }

      stopMicrophoneStream();
      stopMicMeter();

      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        } catch (error) {
          console.error("Unable to stop vocal audio during cleanup:", error);
        }

        audioRef.current = null;
      }

      /*
      Disconnect the currently active SoundFx chain.
    */
      if (vocalFxChainRef.current) {
        try {
          vocalFxChainRef.current.disconnect?.();
        } catch (error) {
          console.error("Unable to disconnect vocal effects:", error);
        }

        vocalFxChainRef.current = null;
      }

      vocalFxSourceRef.current = null;

      /*
      Close the separate Web Audio context used for vocal effects.
    */
      if (vocalFxAudioContextRef.current) {
        const audioContext = vocalFxAudioContextRef.current;

        if (audioContext.state !== "closed") {
          audioContext.close().catch((error) => {
            console.error(
              "Unable to close vocal effects audio context:",
              error,
            );
          });
        }

        vocalFxAudioContextRef.current = null;
      }

      /*
      Revoke temporary local recording URLs.
      Do not revoke remote Firebase audio URLs.
    */
      vocalsRef.current.forEach((vocal) => {
        if (vocal.localUrl?.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(vocal.localUrl);
          } catch (error) {
            console.error("Unable to revoke vocal URL:", error);
          }
        }
      });
    };
  }, []);

  const displayedElapsedMs = isRecording ? recordingElapsedMs : 0;

  const remainingRecordingMs = Math.max(
    0,
    maximumRecordingMs - displayedElapsedMs,
  );

  return (
    <div className="vocals-seq">
      <div className="vocals-header">
        <h3>VOCALS</h3>

        <div className="vocals-pattern-dropdown">
          <select
            value={selectedSavedVocalId}
            onChange={(event) => loadSavedVocal(event.target.value)}
            disabled={isLoadingVocals}
          >
            <option value="">
              {isLoadingVocals ? "Loading vocals..." : "Select saved vocal"}
            </option>

            {savedVocals.map((vocal) => (
              <option key={vocal.id} value={vocal.id}>
                {vocal.name}
              </option>
            ))}
          </select>
        </div>
        <div className="vocal-step-toggle">
          {VOCAL_STEP_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={vocalStepCount === option ? "active" : ""}
              disabled={isRecording}
              onClick={() => {
                setVocalStepCount(option);
                setRecordingElapsedMs(0);
              }}
            >
              {option}
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`vocal-rec-btn ${isRecording ? "recording" : ""}`}
          onClick={isRecording ? stopRecording : startRecording}
        >
          <FaMicrophone />

          {isRecording ? "Stop Recording" : "Record"}
        </button>

        <button
          type="button"
          className="vocal-control-btn"
          onClick={() => setMuted((previous) => !previous)}
        >
          {muted ? <FaVolumeMute /> : <FaVolumeUp />}

          {muted ? "Muted" : "Mute"}
        </button>

        <input
          className="vocal-volume"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={(event) => setVolume(Number(event.target.value))}
        />

        <div className="mic-meter">
          <div style={{ width: `${micLevel}%` }} />
        </div>
      </div>

      <div
        className={`vocal-recording-clock ${isRecording ? "recording" : ""}`}
      >
        <div
          className="vocal-clock-circle"
          style={{
            background: `conic-gradient(
              #ff164d ${recordingProgress}%,
              rgba(255, 255, 255, 0.08) ${recordingProgress}%
            )`,
          }}
        >
          <div className="vocal-clock-inner">
            <FaClock />

            <strong>{formatTime(displayedElapsedMs)}</strong>

            <span>/ {formatTime(maximumRecordingMs)}</span>
          </div>
        </div>

        <div className="vocal-clock-details">
          <div className="vocal-clock-title">
            {isRecording ? "Recording vocal..." : "Vocal recording length"}
          </div>

          <div className="vocal-clock-meta">
            <span>{vocalStepCount} steps</span>
            <span>{bpm} BPM</span>
            <span>Remaining: {formatTime(remainingRecordingMs)}</span>
          </div>

          <div className="vocal-recording-progress">
            <div
              style={{
                width: `${recordingProgress}%`,
              }}
            />
          </div>

          <div className="vocal-clock-message">
            {isRecording
              ? `Recording will stop automatically at ${formatTime(
                  maximumRecordingMs,
                )}.`
              : `Choose 16, 32 or 64 steps, then press Record.`}
          </div>
        </div>
      </div>

      <div className="vocal-list">
        {vocals.length === 0 && (
          <div className="empty-vocal">
            No vocals yet. Select the length and press Record.
          </div>
        )}

        {vocals.map((vocal) => {
          const vocalIsPlaying = playingVocalId === vocal.id && isPlaying;

          return (
            <div className="vocal-item" key={vocal.id}>
              <div className="vocal-info">
                <input
                  value={vocal.name}
                  onChange={(event) => {
                    const newName = event.target.value;

                    setVocals((previousVocals) =>
                      previousVocals.map((currentVocal) =>
                        currentVocal.id === vocal.id
                          ? {
                              ...currentVocal,
                              name: newName,
                            }
                          : currentVocal,
                      ),
                    );
                  }}
                />

                <span>{vocal.stepCount} steps</span>

                <span>{formatTime(vocal.durationMs)}</span>

                {vocal.saved && <span className="saved-pill">Saved</span>}
              </div>

              <div className="wave-line">
                {(vocal.waveform?.length
                  ? vocal.waveform
                  : Array.from({ length: 64 }, () => 8)
                ).map((height, index) => (
                  <span
                    key={index}
                    style={{
                      height: `${height}px`,
                    }}
                  />
                ))}
              </div>

              <div className="vocal-item-volume">
                <button type="button" onClick={() => toggleVocalMute(vocal.id)}>
                  {vocal.muted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={vocal.volume ?? volume}
                  onChange={(event) =>
                    updateVocalVolume(vocal.id, Number(event.target.value))
                  }
                />

                <SoundFxControls
                  effects={vocal.effects || SoundFx.getDefaultEffects()}
                  onChange={(nextEffects) =>
                    updateVocalEffects(vocal.id, nextEffects)
                  }
                  compact
                  showOnlyEq
                />
              </div>

              <SoundFxControls
                effects={vocal.effects || SoundFx.getDefaultEffects()}
                onChange={(nextEffects) =>
                  updateVocalEffects(vocal.id, nextEffects)
                }
                hideEq
              />

              <div className="vocal-actions">
                <button type="button" onClick={() => playVocal(vocal)}>
                  {vocalIsPlaying ? <FaPause /> : <FaPlay />}
                </button>

                <button type="button" onClick={stopVocal}>
                  <FaStop />
                </button>
                <button
                  type="button"
                  onClick={() => saveVocal(vocal)}
                  title={
                    vocal.saved
                      ? "Process effects and replace saved vocal"
                      : "Process effects and save vocal"
                  }
                >
                  <FaSave />
                </button>

                <button type="button" onClick={() => deleteVocal(vocal.id)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VocalsSeq;
